import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const TYPES = ['Theft / Break-in','Vandalism','Injury / Accident','Suspicious Person','Equipment Failure','Food Safety Concern','Pest / Disease Outbreak','Chemical Spill','Power Failure','Other']

export default function IncidentReports() {
  const { user, profile, canEditData } = useAuth()
  const [tab, setTab] = useState('new')
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  const [form, setForm] = useState({
    incident_date: ugandaDatetime(),
    incident_type: '',
    description: '',
    evidence: '',
    action_taken: '',
    follow_up: '',
    police_called: false,
  })

  useEffect(() => { if (tab === 'history') loadReports() }, [tab])

  const loadReports = async () => {
    setLoading(true)
    const { data } = await supabase.from('incident_reports')
      .select('*, profiles(full_name)')
      .not('incident_type', 'eq', 'Quality Audit')
      .not('incident_type', 'eq', 'Delivery Log')
      .order('incident_date', { ascending: false }).limit(30)
    setReports(data || [])
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.incident_type || !form.description) { setError('Incident type and description are required.'); return }
    setSaving(true); setError('')
    const { error: err } = await supabase.from('incident_reports').insert({ ...form, user_id: user.id })
    if (err) { setError(err.message); setSaving(false); return }
    setSaved(true); setSaving(false)
    setTimeout(() => setSaved(false), 3000)
    setForm({ incident_date: ugandaDatetime(), incident_type: '', description: '', evidence: '', action_taken: '', follow_up: '', police_called: false })
    setTab('history'); loadReports()
  }

  const severityColor = (type) => {
    if (['Theft / Break-in','Injury / Accident','Chemical Spill'].includes(type)) return 'badge-red'
    if (['Food Safety Concern','Pest / Disease Outbreak','Power Failure'].includes(type)) return 'badge-amber'
    return 'badge-grey'
  }

  return (
    <AppLayout title="Incident Reports">
      <div className="section-header">
        <div className="section-icon">🚨</div>
        <div className="section-info">
          <h2>Incident Reports</h2>
          <p>Report any accident, security issue, equipment failure, or emergency. Log it even if it seems minor.</p>
        </div>
        {canEditData && <button className="btn btn-danger" style={{ marginLeft: 'auto' }} onClick={() => setTab('new')}>🚨 Report Incident</button>}
      </div>
      <div className="alert alert-red">🚨 For life-threatening emergencies — call emergency services first, then report here.</div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>📝 New Report</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={() => setTab('history')}>📚 History</button>
        <button className={`tab ${tab==='guide'?'active':''}`} onClick={() => setTab('guide')}>📖 Response Guide</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Incident reported and saved.</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group"><label className="form-label">Date & Time of Incident *</label><input className="form-input" type="datetime-local" value={form.incident_date} onChange={e => setForm(f=>({...f,incident_date:e.target.value}))} disabled={!canEditData} /></div>
            <div className="form-group"><label className="form-label">Type of Incident *</label>
              <select className="form-select" value={form.incident_type} onChange={e => setForm(f=>({...f,incident_type:e.target.value}))} disabled={!canEditData}>
                <option value="">Select type...</option>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group"><label className="form-label">Detailed Description *</label><textarea className="form-textarea" style={{ minHeight: 100 }} placeholder="Describe exactly what happened, in what order, who was involved, what was seen or heard..." value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} disabled={!canEditData} /></div>
          <div className="form-group"><label className="form-label">Evidence / Photos Taken</label><textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="List what was documented, photographed, or witnessed..." value={form.evidence} onChange={e => setForm(f=>({...f,evidence:e.target.value}))} disabled={!canEditData} /></div>
          <div className="form-group"><label className="form-label">Action Taken</label><textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="What did you do in response?" value={form.action_taken} onChange={e => setForm(f=>({...f,action_taken:e.target.value}))} disabled={!canEditData} /></div>
          <div className="form-group"><label className="form-label">Follow-up Required</label><textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="What needs to happen next? Who is responsible?" value={form.follow_up} onChange={e => setForm(f=>({...f,follow_up:e.target.value}))} disabled={!canEditData} /></div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: 12, background: form.police_called ? 'var(--red-pale)' : 'var(--cream)', borderRadius: 8, border: '1.5px solid var(--border)', cursor: 'pointer' }} onClick={() => canEditData && setForm(f=>({...f,police_called:!f.police_called}))}>
            <span style={{ fontSize: '1.2rem' }}>{form.police_called ? '✅' : '⬜'}</span>
            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Police / Emergency services were called</span>
          </div>

          <div className="form-group"><label className="form-label">Reported by</label><input className="form-input" value={profile?.full_name || ''} readOnly style={{ background: '#f0f0e8', maxWidth: 240 }} /></div>
          {canEditData && <button className="btn btn-danger btn-lg" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" />Saving...</> : '🚨 Submit Report'}</button>}
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="card-title">Incident History</div>
          {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          : reports.length === 0 ? <div className="empty-state"><div className="empty-icon">🚨</div><h3>No incidents reported</h3><p>This is a good sign!</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Police?</th><th>Reported By</th></tr></thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: '0.78rem', fontWeight: 600 }}>{new Date(r.incident_date).toLocaleDateString('en-UG')}</td>
                      <td><span className={`badge ${severityColor(r.incident_type)}`}>{r.incident_type}</span></td>
                      <td style={{ fontSize: '0.78rem', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description}</td>
                      <td>{r.police_called ? <span className="badge badge-red">Yes</span> : <span className="badge badge-grey">No</span>}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.profiles?.full_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'guide' && (
        <div>
          {[
            { title: '🔥 Fire', color: 'var(--red)', steps: ['Evacuate immediately — do not try to fight a large fire', 'Call 999 / local emergency number — give your address', 'Do not go back inside for any reason', 'Call Edward once you are safe', 'Call insurance company as soon as possible'] },
            { title: '💧 Flood / Major Water Leak', color: 'var(--blue)', steps: ['Turn off water and electricity at the mains — electricity + water is deadly', 'Call Edward immediately', 'Photograph everything before cleaning up — for insurance', 'Call a plumber and your insurance company'] },
            { title: '⚡ Power Failure', color: 'var(--amber)', steps: ['Check fuse box and circuit breakers', 'Call electricity provider to report the outage', 'After 2 hours — begin emergency harvesting of near-ready produce', 'Monitor fridge temperature — add ice if needed after 4 hours', 'Contact Edward if outage continues beyond 2 hours'] },
            { title: '🤕 Injury on Farm', color: 'var(--red)', steps: ['Serious injury — call emergency services immediately', 'Minor injury — use the first aid kit (location: ___)', 'Chemical exposure — flush with water for 15+ minutes, call Poison Control', 'Do not move an injured person unless in immediate danger', 'Complete an Incident Report as soon as the person is safe'] },
            { title: '👤 Break-In / Theft', color: '#555', steps: ['Do NOT enter if you suspect someone is inside — stay outside', 'Call police immediately — do not touch anything', 'Call Edward', 'Photograph everything after police clear the scene', 'Call insurance company within 24 hours'] },
          ].map(({ title, color, steps }) => (
            <div key={title} className="card" style={{ borderLeft: `4px solid ${color}`, marginBottom: 14 }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', fontWeight: 700, color, marginBottom: 12 }}>{title}</div>
              {steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f5f5f0', fontSize: '0.83rem' }}>
                  <div style={{ width: 22, height: 22, minWidth: 22, background: color, color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>{i+1}</div>
                  {step}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  )
}
